import { RegistryWorkspace, RegistryTableSurface } from '@workspace/silk-ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useScope } from '@workspace/scope-context';
import {
  CommerceCatalogWorkspace,
  DataTable,
  DataTableToolbar,
  DataTableFacetedFilter,
  Checkbox,
  DescriptionText,
  HorizontalStack,
  RegistryStatus,
  Surface,
  VerticalStack,
  type ColumnDef,
  type ColumnFiltersState,
  type OnChangeFn,
  type RowSelectionState,
  type SortingState,
} from '@workspace/silk-ui';
import { useNavigation } from '@/context/NavigationContext';
import {
  CommerceBreadcrumbHeader,
  buildCommerceSectionRoute,
  buildCommerceShellViewModel,
} from '@/views/commerce/catalogHierarchyShared';
import { lastPageIndexForTotal, toLimitOffset } from '@/lib/routePagination';
import { useCommerceRegistryQuery } from '@/hooks/useCommerceRegistryQuery';
import { defineCommerceRegistryColumns } from '@/lib/commerceRegistryColumns';

const CART_SORT_FIELDS = ['cart_id', 'status', 'total', 'created_at', 'updated_at'] as const;

import { ShoppingCart } from 'lucide-react';

import {
  listCarts,
  getCommerceErrorMessage,
  getCommerceErrorStatus,
  type Cart,
  type CartWorkspace,
  type CommerceFacetBucket,
} from '@/lib/commerce';

function formatCartTotal(cart: Cart): string {
  const pricing = cart.pricing_snapshot ?? {};
  const currency = typeof pricing.currency === 'string' && pricing.currency.trim() ? pricing.currency.trim() : 'USD';
  const grandTotal = typeof pricing.grand_total === 'number' ? pricing.grand_total : null;
  const recurringTotal = typeof pricing.recurring_total === 'number' ? pricing.recurring_total : 0;
  const dueTodayTotal =
    typeof pricing.due_today_total === 'number'
      ? pricing.due_today_total
      : typeof pricing.startup_total === 'number'
        ? pricing.startup_total
        : typeof pricing.total_due_today === 'number'
          ? pricing.total_due_today
          : typeof pricing.startup_fee_total === 'number'
            ? pricing.startup_fee_total
            : 0;
  const resolvedMinor = grandTotal ?? recurringTotal + dueTodayTotal;
  return `${currency} ${(resolvedMinor / 100).toFixed(2)}`;
}

function cartAddress(cart: Cart): string | null {
  const serviceAddress = cart.service_address_snapshot ?? {};
  const formattedAddress = typeof serviceAddress.formatted_address === 'string' ? serviceAddress.formatted_address.trim() : '';
  if (formattedAddress) {
    return formattedAddress;
  }
  const address = typeof serviceAddress.address === 'string' ? serviceAddress.address.trim() : '';
  return address || null;
}

function statusVariant(status?: string | null): 'default' | 'success' | 'warning' | 'error' | 'muted' {
  switch ((status || '').toLowerCase()) {
    case 'active':
    case 'open':
      return 'success';
    case 'expired':
    case 'abandoned':
      return 'muted';
    case 'converted':
    case 'completed':
      return 'default';
    default:
      return 'default';
  }
}

const CART_STATUS_VALUES = [
  'active',
  'open',
  'submitted',
  'converted',
  'completed',
  'expired',
  'abandoned',
] as const;

export default function CartsPage({ workspace }: { workspace: CartWorkspace }) {
  const { activeTenant } = useScope();
  const navigation = useNavigation();
  const [carts, setCarts] = useState<Cart[]>([]);
  const [totalCarts, setTotalCarts] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accessDeniedStatus, setAccessDeniedStatus] = useState<number | null>(null);
  const query = useCommerceRegistryQuery({
    facetKeys: ['status'],
    facetValues: { status: CART_STATUS_VALUES },
    sortFields: CART_SORT_FIELDS,
  });
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [statusOptions, setStatusOptions] = useState<CommerceFacetBucket[]>([]);
  const requestSequence = useRef(0);

  const columnFilters = useMemo<ColumnFiltersState>(() =>
    query.facets.status.length > 0 ? [{ id: 'status', value: query.facets.status }] : [],
  [query.facets.status]);
  const sorting = useMemo<SortingState>(() => query.sort.map(token => ({
    id: token.startsWith('-') ? token.slice(1) : token,
    desc: token.startsWith('-'),
  })), [query.sort]);

  const selectedStatuses = useMemo(() => {
    return query.facets.status;
  }, [query.facets.status]);

  const loadData = async () => {
    const requestID = ++requestSequence.current;
    if (!activeTenant?.id) {
      setCarts([]);
      setTotalCarts(0);
      setError(null);
      setAccessDeniedStatus(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setAccessDeniedStatus(null);
    try {
      const paginationQuery = toLimitOffset(query.pagination);
      const cartPage = await listCarts(workspace, {
        ...paginationQuery,
        ...(query.search ? { search: query.search } : {}),
        ...(selectedStatuses.length > 0 ? { status: selectedStatuses } : {}),
        ...(query.sort.length > 0 ? { sort: query.sort } : {}),
        include_facets: ['status'],
      });
      if (requestID !== requestSequence.current) return;
      const nextPageIndex = lastPageIndexForTotal(cartPage.total_count, query.pagination.pageSize);
      if (query.pagination.pageIndex > nextPageIndex) {
        query.onPaginationChange(current => ({ ...current, pageIndex: nextPageIndex }));
        return;
      }
      setCarts(cartPage.items);
      setTotalCarts(cartPage.total_count);
      setStatusOptions(cartPage.facets?.status ?? []);
    } catch (err) {
      if (requestID !== requestSequence.current) return;
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) setAccessDeniedStatus(status);
      else setError(getCommerceErrorMessage(err, 'Failed to load carts.'));
      setCarts([]);
      setTotalCarts(0);
    } finally {
      if (requestID === requestSequence.current) setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, [activeTenant?.id, workspace, query.pagination.pageIndex, query.pagination.pageSize, query.search, query.sort.join('\u0000'), selectedStatuses.join('\u0000')]);

  useEffect(() => {
    setRowSelection({});
  }, [query.pagination.pageIndex, query.pagination.pageSize, query.search, query.sort.join('\u0000'), selectedStatuses.join('\u0000')]);

  const handleSortingChange: OnChangeFn<SortingState> = updater => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    query.setSort(next.map(item => `${item.desc ? '-' : ''}${item.id}`));
  };

  const columns = useMemo<ColumnDef<Cart>[]>(() => [
    {
      id: 'select',
      size: 40,
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'cart_id',
      header: 'Cart',
      size: 300,
      cell: ({ row }) => {
        const cart = row.original;
        const address = cartAddress(cart);
        return (
          <div className="flex flex-col gap-0.5">
            <p className="text-sm font-black text-foreground">
              Cart {cart.cart_id}
            </p>
            {address && (
              <DescriptionText className="line-clamp-1 text-[11px]">
                {address}
              </DescriptionText>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 150,
      cell: ({ row }) => (
        <RegistryStatus 
          label={row.original.status} 
          variant={statusVariant(row.original.status)} 
        />
      ),
      filterFn: (row, id, value) => value.includes(row.getValue(id)),
    },
    {
      id: 'total',
      accessorFn: cart => Number(
        cart.pricing_snapshot?.grand_total
        ?? cart.pricing_snapshot?.due_today_total
        ?? cart.pricing_snapshot?.startup_total
        ?? 0,
      ),
      header: 'Estimated Total',
      size: 150,
      cell: ({ row }) => (
        <span className="text-[11px] font-black text-primary">
          {formatCartTotal(row.original)}
        </span>
      ),
    },
    {
      accessorKey: 'updated_at',
      header: 'Updated',
      size: 150,
      cell: ({ row }) => (
        <span className="text-[11px] font-bold text-foreground/80">
          {new Date(row.original.updated_at).toLocaleDateString()}
        </span>
      ),
    },
  ], []);
  defineCommerceRegistryColumns(columns, { mode: 'manual', sortFields: CART_SORT_FIELDS, registry: 'carts' });

  const workspaceTitle = workspace === 'catalog-owner' ? 'Commerce Carts' : 'Reseller Carts';
  const workspaceDescription =
    workspace === 'catalog-owner'
      ? 'Review active checkout sessions across every selling source for the active catalog owner tenant.'
      : 'Review active checkout sessions created by the active reseller tenant.';

  const shellViewModel = buildCommerceShellViewModel({
    activeTenant,
    accessDeniedStatus,
    activeSectionId: 'carts',
    searchQuery: query.search,
    searchPlaceholder: 'Search carts...',
    onSearchChange: query.setSearch,
    error,
    blockingReason: 'Your current role does not have permission to review carts.',
    onSelectSection: sectionId =>
      navigation.navigate(
        sectionId === 'carts' && workspace === 'reseller'
          ? '/reseller/carts'
          : sectionId === 'orders' && workspace === 'reseller'
            ? '/reseller/orders'
            : buildCommerceSectionRoute(sectionId)
      ),
    primaryAction: {
      id: 'refresh-carts',
      label: 'Refresh',
      onAction: () => void loadData(),
      disabled: loading,
    },
    metrics: [
      { id: 'cart-total', label: 'Loaded Carts', value: String(carts.length) },
      { id: 'cart-active', label: 'Loaded Active', value: String(carts.filter(c => c.status === 'active').length) },
    ]
  });

  return (
    <CommerceCatalogWorkspace
      viewModel={shellViewModel}
      padded={false}
      titleContent={
        <CommerceBreadcrumbHeader
          breadcrumbs={[{ label: 'Carts' }]}
          title={workspaceTitle}
          description={workspaceDescription}
        />
      }
    >
      <RegistryWorkspace>
        <RegistryTableSurface>
          <DataTable
            mode="manual"
            columns={columns}
            data={carts}
            sorting={sorting}
            onSortingChange={handleSortingChange}
            columnFilters={columnFilters}
            onColumnFiltersChange={(updater) => {
              const next = typeof updater === 'function' ? updater(columnFilters) : updater;
              const statusFilter = next.find(entry => entry.id === 'status');
              query.setFacet('status', Array.isArray(statusFilter?.value) ? statusFilter.value.map(String) : []);
            }}
            globalFilter={query.search}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            pagination={query.pagination}
            onPaginationChange={query.onPaginationChange}
            manualPagination={true}
            totalRowCount={totalCarts}
            loading={loading}
            className="border-none rounded-none"
            paginationClassName="px-0 py-0"
            showPagination={true}
            emptyState={
              <div className="px-0 py-12 text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted/30">
                  <ShoppingCart className="h-6 w-6 text-muted-foreground/60" />
                </div>
                <p className="text-sm font-black text-foreground">No Active Carts</p>
                <DescriptionText className="mt-1 text-xs">
                  {query.search || selectedStatuses.length > 0
                    ? 'No carts match the current search or filters.'
                    : 'Active checkout sessions will appear here.'}
                </DescriptionText>
              </div>
            }
            renderToolbar={(table) => (
              <DataTableToolbar
                table={table}
                searchValue={query.search}
                onSearchChange={query.setSearch}
                searchPlaceholder="Search all carts..."
                resultCount={totalCarts}
                className="border-b border-border/40 bg-transparent px-0 py-0"
                filters={
                  table.getColumn('status') && (
                    <DataTableFacetedFilter
                      title="Status"
                      selectedValues={new Set(table.getColumn('status')?.getFilterValue() as string[] ?? [])}
                      onFilterChange={(values) => table.getColumn('status')?.setFilterValue(Array.from(values))}
                      options={statusOptions.map(option => ({
                        label: option.label || option.value,
                        value: option.value,
                      }))}
                      counts={Object.fromEntries(statusOptions.map(option => [option.value, option.count]))}
                    />
                  )
                }
              />
            )}
          />
        </RegistryTableSurface>
      </RegistryWorkspace>
    </CommerceCatalogWorkspace>
  );
}
